import { motion, AnimatePresence } from 'framer-motion';
import type { Character, Move } from '@/types';
import { CLASS_DATA } from '@/types';

interface AbilitiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  character: Character | null;
}

interface ClassAbility {
  name: string;
  description: string;
  icon: string;
}

// Base abilities for each class
const CLASS_ABILITIES: Record<string, ClassAbility[]> = {
  paladin: [
    {
      name: 'Возложение рук',
      description: 'Когда ты касаешься кого-то и молишься за исцеление, бросай +ХАР. На 10+ исцеляешь 1d8 урона. На 7-9 исцеляешь, но привлекаешь нежелательное внимание.',
      icon: '✋',
    },
    {
      name: 'Бронированный',
      description: 'Ты игнорируешь тег «неуклюжий» на любой броне.',
      icon: '🛡️',
    },
    {
      name: 'Я — Закон',
      description: 'Когда ты даёшь NPC приказ, основываясь на своём божественном авторитете, бросай +ХАР. На 10+ они выбирают: сделать что ты говоришь или быть отмеченными как враги веры. На 7-9 ты также привлекаешь нежелательное внимание.',
      icon: '⚖️',
    },
    {
      name: 'Поход',
      description: 'Когда ты посвящаешь себя миссии через молитву и получаешь знак от божества, ты получаешь задание. Пока выполняешь задание — получаешь преимущество на связанные действия.',
      icon: '🏰',
    },
  ],
  fighter: [
    {
      name: 'Гнуть решётки, поднимать врата',
      description: 'Когда ты используешь грубую силу, чтобы уничтожить неодушевлённое препятствие, бросай +СИЛ. На 10+ выбери 3, на 7-9 выбери 2: не занимает много времени, ничего ценного не повреждено, не создаёт много шума, можно починить.',
      icon: '💪',
    },
    {
      name: 'Бронированный',
      description: 'Ты игнорируешь тег «неуклюжий» на любой броне.',
      icon: '🛡️',
    },
    {
      name: 'Фирменное оружие',
      description: 'Это твоё оружие. Их много, но это — твоё. Твоё оружие это инструмент убийства, известный всюду. Выбери базу и улучшения для своего фирменного оружия.',
      icon: '⚔️',
    },
  ],
  thief: [
    {
      name: 'Эксперт по ловушкам',
      description: 'Когда ты тратишь время на осмотр опасной области, бросай +ЛОВ. На 10+ узнаёшь 3 детали, на 7-9 узнаёшь 1: есть ли ловушка, что её активирует, что она делает, что ещё здесь скрыто.',
      icon: '🔓',
    },
    {
      name: 'Уловки ремесла',
      description: 'Когда ты вскрываешь замки или обезвреживаешь ловушки, бросай +ЛОВ. На 10+ делаешь это без проблем. На 7-9 делаешь, но GM предложит выбор: оставляешь следы, занимает время, или что-то сломано.',
      icon: '🗝️',
    },
    {
      name: 'Удар в спину',
      description: 'Когда ты атакуешь ничего не подозревающего или беспомощного врага ближним оружием, ты можешь выбрать: нанести урон или бросить +ЛОВ. На 10+ выбери 2, на 7-9 выбери 1: не шумишь, не попадаешь в опасность, наносишь +1d6 урона.',
      icon: '🗡️',
    },
    {
      name: 'Гибкая мораль',
      description: 'Когда кто-то пытается определить твоё мировоззрение, ты можешь сказать им то, что они хотят услышать.',
      icon: '🎭',
    },
    {
      name: 'Отравитель',
      description: 'Ты освоил использование ядов. Выбери яд из списка. Этот яд больше не опасен для тебя.',
      icon: '☠️',
    },
  ],
  ranger: [
    {
      name: 'Выследить',
      description: 'Когда ты следуешь по следам, бросай +МДР. На 10+ следуешь по следам до того места, где оставивший их резко сменил направление. На 7-9 выбираешь: следуешь далеко но вслепую, или близко но точно.',
      icon: '🐾',
    },
    {
      name: 'Звериный компаньон',
      description: 'У тебя есть верное животное-компаньон. Выбери его вид и способности. Оно всегда рядом и выполняет твои команды.',
      icon: '🐺',
    },
    {
      name: 'Прицельный выстрел',
      description: 'Когда ты целишься и стреляешь, бросай +ЛОВ. На 10+ наносишь урон и выбираешь одно: пробиваешь броню (-1 к броне), прикрепляешь к цели, отключаешь конечность. На 7-9 наносишь урон.',
      icon: '🎯',
    },
    {
      name: 'Команда',
      description: 'Когда ты отдаёшь команду своему животному-компаньону, бросай +МДР. На 10+ выполняет и выбирает 1, на 7-9 выполняет и выбирает 2: отвлекается, пугается, злится.',
      icon: '📢',
    },
  ],
  bard: [
    {
      name: 'Чарующее представление',
      description: 'Когда ты выступаешь перед аудиторией, бросай +ХАР. На 10+ выбери 3, на 7-9 выбери 1: завораживаешь слушателей, вдохновляешь союзника (+1 вперёд), деморализуешь врага (-1 вперёд).',
      icon: '🎭',
    },
    {
      name: 'Широкие познания',
      description: 'Когда ты впервые входишь в важное место, можешь задать GM один вопрос из списка Чтения ситуации.',
      icon: '📚',
    },
    {
      name: 'Песнь исцеления',
      description: 'Когда ты исцеляешь кого-то песней, он восстанавливает 1d8 HP.',
      icon: '🎵',
    },
    {
      name: 'Переговорщик',
      description: 'Когда ты ведёшь переговоры в чью-то пользу, получаешь +1.',
      icon: '💬',
    },
  ],
  wizard: [
    {
      name: 'Сотворение заклинания',
      description: 'Когда ты творишь заклинание, которое выучил и подготовил, бросай +ИНТ. На 10+ заклинание успешно сотворено. На 7-9 выбери одно: привлекаешь нежелательное внимание, заклинание также нацелено на другое, заклинание забыто.',
      icon: '✨',
    },
    {
      name: 'Книга заклинаний',
      description: 'У тебя есть книга заклинаний. В ней записаны все известные тебе заклинания. Начинаешь с тремя заклинаниями 1 уровня.',
      icon: '📖',
    },
    {
      name: 'Подготовка заклинаний',
      description: 'После отдыха ты можешь подготовить заклинания из книги. Количество подготовленных заклинаний ограничено уровнем + 1.',
      icon: '🔮',
    },
  ],
  cleric: [
    {
      name: 'Божество',
      description: 'Ты служишь божеству и следуешь его принципам. Выбери своё божество и его домены.',
      icon: '⛪',
    },
    {
      name: 'Божественное руководство',
      description: 'Когда ты просишь своё божество о руководстве, даже в самых тёмных местах, ты получаешь знак.',
      icon: '🙏',
    },
    {
      name: 'Изгнание нежити',
      description: 'Когда ты поднимаешь святой символ и призываешь божество, чтобы изгнать нежить, бросай +МДР. На 10+ нежить убегает или уничтожена. На 7-9 она отступает или получает урон.',
      icon: '☀️',
    },
    {
      name: 'Причастие',
      description: 'Когда ты проводишь ритуал общения с божеством, можешь задать вопрос и получить ответ.',
      icon: '💫',
    },
  ],
  druid: [
    {
      name: 'Смена облика',
      description: 'Когда ты призываешь духов земли, чтобы изменить свой облик, бросай +МДР. На 10+ удерживаешь 3, на 7-9 удерживаешь 2. Трать удержание на особые ходы существа.',
      icon: '🐻',
    },
    {
      name: 'Рождённый в диких землях',
      description: 'Выбери свою землю: леса, степи, пустоши, болота, горы, море. Там ты дома, и духи говорят с тобой.',
      icon: '🌲',
    },
    {
      name: 'Дух природы',
      description: 'Дикие животные не атакуют тебя без провокации.',
      icon: '🦌',
    },
    {
      name: 'Изучение духов',
      description: 'Когда ты изучаешь зверя, духа или растение, узнаёшь один полезный факт о нём.',
      icon: '🍃',
    },
  ],
  barbarian: [
    {
      name: 'Гнев берсерка',
      description: 'Когда ты впадаешь в ярость, получаешь +1d4 к урону, но -1 к защите. Ярость длится до конца боя.',
      icon: '😤',
    },
    {
      name: 'Несокрушимый',
      description: 'Когда ты терпишь урон, можешь вычесть из него свой уровень.',
      icon: '💪',
    },
    {
      name: 'Аппетит к разрушению',
      description: 'Выбери: крошить, калечить, терроризировать. Получаешь +1 когда делаешь выбранное.',
      icon: '💥',
    },
  ],
  immolator: [
    {
      name: 'Пламенное рождение',
      description: 'Ты был крещён огнём. Огонь не может причинить тебе вред.',
      icon: '🔥',
    },
    {
      name: 'Сжечь дотла',
      description: 'Когда ты призываешь огонь, чтобы уничтожить что-либо, бросай +КОН. На 10+ оно сгорает полностью. На 7-9 загорается, но огонь может распространиться.',
      icon: '🌋',
    },
    {
      name: 'Зов пепла',
      description: 'Когда ты шепчешь пеплу, он может рассказать о том, что сгорело.',
      icon: '💨',
    },
  ],
};

function getClassTitle(characterClass: string | null): { title: string; icon: string } {
  switch (characterClass) {
    case 'wizard': return { title: 'Заклинания', icon: '📖' };
    case 'cleric': return { title: 'Молитвы и Домены', icon: '⛪' };
    case 'druid': return { title: 'Силы природы', icon: '🌿' };
    case 'bard': return { title: 'Песни и Легенды', icon: '🎵' };
    case 'paladin': return { title: 'Клятвы и Благословения', icon: '⚔️' };
    case 'immolator': return { title: 'Пламенные силы', icon: '🔥' };
    case 'fighter': return { title: 'Боевые приёмы', icon: '⚔️' };
    case 'thief': return { title: 'Трюки и Уловки', icon: '🗡️' };
    case 'ranger': return { title: 'Искусство следопыта', icon: '🏹' };
    case 'barbarian': return { title: 'Ярость варвара', icon: '💪' };
    default: return { title: 'Способности', icon: '⭐' };
  }
}

function getClassNameRu(characterClass: string | null): string {
  return characterClass ? (CLASS_DATA[characterClass as keyof typeof CLASS_DATA]?.nameRu || characterClass) : '';
}

function getMoveIcon(move: Move): string {
  const name = move.name.toLowerCase();

  if (name.includes('удар') || name.includes('атак') || name.includes('руби')) return '⚔️';
  if (name.includes('защит') || name.includes('блок')) return '🛡️';
  if (name.includes('стрел') || name.includes('залп') || name.includes('выстрел')) return '🏹';
  if (name.includes('закл') || name.includes('магия') || name.includes('чары')) return '✨';
  if (name.includes('молит') || name.includes('исцел') || name.includes('лечен')) return '💫';
  if (name.includes('огон') || name.includes('пламя') || name.includes('испепел')) return '🔥';
  if (name.includes('крад') || name.includes('скрыт') || name.includes('тень')) return '🌙';
  if (name.includes('ловуш') || name.includes('замок') || name.includes('капкан')) return '🔓';
  if (name.includes('зверь') || name.includes('животн') || name.includes('облик')) return '🐺';
  if (name.includes('природ') || name.includes('лес') || name.includes('дерев')) return '🌿';
  if (name.includes('знан') || name.includes('лор') || name.includes('мудр')) return '📚';
  if (name.includes('песн') || name.includes('музык') || name.includes('бард')) return '🎵';
  if (name.includes('перегов') || name.includes('убежд')) return '💬';
  return '⭐';
}

export function AbilitiesModal({ isOpen, onClose, character }: AbilitiesModalProps) {
  if (!character) return null;

  const { title, icon } = getClassTitle(character.characterClass);
  const classAbilities = CLASS_ABILITIES[character.characterClass || ''] || [];
  const dynamicMoves = character.moves || [];
  const classNameRu = getClassNameRu(character.characterClass);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-gray-800 rounded-2xl p-4 w-[90%] max-w-md max-h-[80vh] overflow-hidden border border-gray-700"
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-bold text-amber-500 flex items-center gap-2">
                <span>{icon}</span>
                <span>{title}</span>
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-2xl transition-colors w-8 h-8 flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            {/* Class info */}
            <div className="text-sm text-gray-400 mb-3 px-1 flex items-center gap-2">
              {classNameRu && (
                <span className="bg-gray-700 px-2 py-0.5 rounded">{classNameRu}</span>
              )}
              <span>Уровень {character.level ?? 1}</span>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto max-h-[60vh] space-y-4 pr-1">
              {/* Class Base Abilities */}
              {classAbilities.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide px-1">
                    Базовые способности
                  </h3>
                  {classAbilities.map((ability, index) => (
                    <div
                      key={index}
                      className="bg-gray-700/50 rounded-xl p-3 border border-gray-600/50"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{ability.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium">{ability.name}</div>
                          <div className="text-xs text-gray-400 mt-1 leading-relaxed">
                            {ability.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Dynamic moves from character */}
              {dynamicMoves.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide px-1">
                    Изученные ходы
                  </h3>
                  {dynamicMoves.map((move, index) => (
                    <div
                      key={move.key || index}
                      className="bg-amber-900/20 rounded-xl p-3 border border-amber-600/30"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{getMoveIcon(move)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-amber-400 font-medium">{move.name}</div>
                          {move.description && (
                            <div className="text-xs text-gray-400 mt-1 leading-relaxed">
                              {move.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {classAbilities.length === 0 && dynamicMoves.length === 0 && (
                <div className="text-center text-gray-500 py-12">
                  <span className="text-4xl block mb-2">📜</span>
                  <p>Нет способностей</p>
                  <p className="text-xs mt-2">Способности появятся после создания персонажа</p>
                </div>
              )}
            </div>

            {/* Footer */}
            {(classAbilities.length > 0 || dynamicMoves.length > 0) && (
              <div className="mt-3 pt-3 border-t border-gray-700 text-sm text-gray-400 text-center">
                Всего: {classAbilities.length + dynamicMoves.length} способностей
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
